import { __rest, __assign } from 'tslib';

// Standard page action attribute names for consistent naming conventions.
var SHOPPER_ID = 'shopperId';
var SITE = 'site';
var TENANT = 'tenant';
var LOCALE = 'locale';
var ACTION_ROOT = 'actionRoot';
var ADDITIONAL_ATTRIBUTES = 'additionalAttributes';
// `window` property to store the new relic shared data container.
var TRACKING_CONTAINER = 'omNRTrackingContainer';
var TRACKING_CONTAINER_PROPERTIES = [
    SHOPPER_ID,
    SITE,
    TENANT,
    LOCALE,
    ACTION_ROOT,
];

var windowInvalid = function () { return typeof window === "undefined"; };
/**
 * exploit the naturally recursive nature of JSON.stringify, and pass it a
 * replacer function which runs on each nested object during the stringification
 * process
 *
 * see: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/JSON/stringify#the_replacer_parameter
 */
function deleteInNestedObject(entireObj, keyToFind) {
    JSON.stringify(entireObj, function (_, nestedValue) {
        if (nestedValue && nestedValue[keyToFind]) {
            delete nestedValue[keyToFind];
        }
        return nestedValue;
    });
    return entireObj;
}
/**
 * A helper function to indicate whether or not the tracking container is properly initialized on the window.
 *
 * @returns boolean indicating whether or not the tracking container object of the window is defined and
 * has a valid schema of object type and properties.
 */
var isNewRelicTrackerInitialized = function (_a) {
    var _b = _a === void 0 ? {} : _a, _c = _b.debug, debug = _c === void 0 ? false : _c;
    if (windowInvalid()) {
        if (debug) {
            console.error('[NewRelicTracker] `window` is undefined, unable check if New Relic Tracking Container is initialized');
        }
        return false;
    }
    var container = window[TRACKING_CONTAINER];
    if (typeof container !== 'object' ||
        container === null ||
        container === undefined)
        return false;
    var containerValidity = true;
    var properties = Object.keys(container);
    for (var _i = 0, TRACKING_CONTAINER_PROPERTIES_1 = TRACKING_CONTAINER_PROPERTIES; _i < TRACKING_CONTAINER_PROPERTIES_1.length; _i++) {
        var trackingProperty = TRACKING_CONTAINER_PROPERTIES_1[_i];
        if (!properties.includes(trackingProperty)) {
            containerValidity = false;
            break;
        }
    }
    return containerValidity;
};
/** A helper function to indicate whether new relic is available for tracking. (Agent installed, tracking container
 * initialized etc.)
 *
 * @param debug
 */
var isNewRelicAvailable = function (_a) {
    var _b = _a.debug, debug = _b === void 0 ? false : _b;
    if (windowInvalid()) {
        if (debug) {
            console.error('[NewRelicTracker] `window` is undefined, unable to track to new relic.');
        }
        return false;
    }
    if (!window.newrelic ||
        !window.newrelic.noticeError ||
        !window.newrelic.addPageAction) {
        if (debug) {
            console.error('[NewRelicTracker] New Relic Browser Agent is not installed on the application, unable to track to new relic.');
        }
        return false;
    }
    if (!isNewRelicTrackerInitialized()) {
        if (debug) {
            console.error('NewRelicTracker] New Relic Tracking Library has not been initialized, unable to track to new relic.');
        }
        return false;
    }
    return true;
};
/**
 * A generic tracking event to fire page actions to New Relic.
 *
 * If no eventPayload is sent with the trackEvent call, no event will be fired to New Relic.
 *
 * If the tracker has not been initialized (via a call to initializeNewRelicTrackingContainer),
 * the event will not be sent to newrelic.
 *
 * @param {*} eventName - The event identifier. Will surface in new relics `actionName` property as `${ACTION_ROOT}.${eventName}`
 * If not provided, a default event name of `unknown-event-id` will be used.
 * Ex NRQL Query: SELECT * FROM PageAction WHERE actionName = 'om-addresses-ui.address-added-successfully'
 * @param {*} debug - A boolean indicating whether or not console errors should track issue when sending events.
 * @param {*} eventProperties - a "collection" of all remaining properties sent with the trackEvent object.
 *
 * By default, the New Relic Tracker will populate page actions with any available data from the `omNRTrackingContainer`.
 * If the trackEvent's parameter overwrites any of the `omNRTrackingContainer` data, it will take priority.
 *
 */
var trackEvent = function (eventPayload) {
    if (!eventPayload) {
        return;
    }
    var _a = eventPayload.eventName, eventName = _a === void 0 ? 'unknown-event-id' : _a, _b = eventPayload.debug, debug = _b === void 0 ? false : _b, eventProperties = __rest(eventPayload, ["eventName", "debug"]);
    if (!isNewRelicAvailable({ debug: debug }))
        return;
    var _c = window[TRACKING_CONTAINER], shopperId = _c.shopperId, site = _c.site, tenant = _c.tenant, locale = _c.locale, actionRoot = _c.actionRoot, additionalAttributes = _c.additionalAttributes;
    var pageActionName = actionRoot ? "".concat(actionRoot, ".").concat(eventName) : eventName;
    // build the page action attributes json object, allow event properties to override any specified `omNRTrackingContainer` properties.
    var pageActionAttributes = __assign({ shopperId: shopperId, site: site, tenant: tenant, locale: locale, additionalAttributes: additionalAttributes }, eventProperties);
    try {
        window.newrelic.addPageAction(pageActionName, pageActionAttributes);
    }
    catch (error) {
        if (debug) {
            console.error('[NewRelicTracker] Error occured while tracking new relic event.', { pageActionName: pageActionName, pageActionAttributes: pageActionAttributes }, error);
        }
    }
};
/**
 * A generic error tracking function to propagate error ocurrences to New Relic.
 *
 * @param {*} error - REQUIRED: The JavaScript error during execution.
 * @param {*} errorMetadataPayload - OPTIONAL: Additional metadata to enrich the error's data and presentation in New Relic.
 * OPTIONAL: `errorMetadataPayload.errorName`: to override the error's primary identifier(message). This property reflects as
 * the primary description of Errors in the New Relic Browser's JS Error Panel, and should be accurately named.
 * We suggest keeping the errorName consistent, and excludes dynamic data such as shopperIds, timestamps, etc.
 */
var trackError = function (error, errorMetadataPayload) {
    if (errorMetadataPayload === void 0) { errorMetadataPayload = {}; }
    if (!error)
        return;
    var _a = errorMetadataPayload.debug, debug = _a === void 0 ? false : _a, errorName = errorMetadataPayload.errorName, errorMetadata = __rest(errorMetadataPayload, ["debug", "errorName"]);
    if (!isNewRelicAvailable({ debug: debug }))
        return;
    var rootErrorMessage = error.message, rootErrorStack = error.stack, rest = __rest(error, ["message", "stack"]);
    // remove users jwt token from the log
    var originalError = deleteInNestedObject(rest, 'Authorization');
    var _b = window[TRACKING_CONTAINER], shopperId = _b.shopperId, site = _b.site, tenant = _b.tenant, locale = _b.locale, additionalAttributes = _b.additionalAttributes;
    var errorIdentifier = errorName || rootErrorMessage || 'unknown-error-id';
    var modifiedError = new Error(errorIdentifier);
    var errorAttributes = __assign({ shopperId: shopperId, site: site, tenant: tenant, locale: locale, additionalAttributes: additionalAttributes, rootErrorObject: {
            rootErrorMessage: rootErrorMessage,
            rootErrorStack: rootErrorStack,
        }, originalError: originalError }, errorMetadata);
    try {
        window.newrelic.noticeError(modifiedError, errorAttributes);
    }
    catch (err) {
        if (debug) {
            console.error('[NewRelicTracker] Error occurred while tracking new relic error.', { modifiedError: modifiedError, errorAttributes: errorAttributes }, err);
        }
    }
};
/**
 * An initialization function to set static application data which will later be used for events and errors sent to New Relic.
 *
 * @param {*} actionRoot
 * @param {*} shopperId
 * @param {*} tenant
 * @param {*} site
 * @param {*} locale
 * @param {*} additionalAttributes
 * @param {*} debug - if set to true, will provide additional console debug information in case of errors.
 */
var initializeNewRelicTrackingContainer = function (_a) {
    var _b;
    var _c = _a === void 0 ? {} : _a, _d = _c.actionRoot, actionRoot = _d === void 0 ? '' : _d, _e = _c.shopperId, shopperId = _e === void 0 ? '' : _e, _f = _c.tenant, tenant = _f === void 0 ? '' : _f, _g = _c.site, site = _g === void 0 ? '' : _g, _h = _c.locale, locale = _h === void 0 ? '' : _h, _j = _c.additionalAttributes, additionalAttributes = _j === void 0 ? {} : _j, _k = _c.debug, debug = _k === void 0 ? false : _k;
    if (windowInvalid()) {
        if (debug) {
            console.error('[NewRelicTracker] `window` is undefined, unable initializing New Relic Tracking Container!');
        }
        return;
    }
    if (window[TRACKING_CONTAINER]) {
        if (debug) {
            console.log('[NewRelicTracker] New Relic Tracking Container is already initialized, overwriting with new init props!');
        }
    }
    window[TRACKING_CONTAINER] = (_b = {},
        _b[ACTION_ROOT] = actionRoot,
        _b[SHOPPER_ID] = shopperId,
        _b[TENANT] = tenant,
        _b[SITE] = site,
        _b[LOCALE] = locale,
        _b[ADDITIONAL_ATTRIBUTES] = additionalAttributes,
        _b);
};
/**
 * A function to add additional attributes to the tracking container after initialization.
 * For example, if we want to track the active experiments for a shopper (and what variation are were in):
 * additionalAttributes = {
 *     activeExperiments: 'om_ab_test:variation_1,om_ab_test_2:variation_2'
 * }
 *
 * @param additionalAttributes
 * @param debug
 */
var addAdditionalAttributes = function (additionalAttributes, debug) {
    var _a;
    if (additionalAttributes === void 0) { additionalAttributes = {}; }
    if (debug === void 0) { debug = false; }
    if (windowInvalid()) {
        if (debug) {
            console.error('[NewRelicTracker] `window` is undefined, unable to add additional attributes to New Relic Tracking Container.');
        }
        return;
    }
    if (!isNewRelicTrackerInitialized()) {
        if (debug) {
            console.error('[NewRelicTracker] New Relic Tracking Library has not been initialized, unable to add additional attributes to the New Relic Tracking Container.');
        }
        return;
    }
    var currentlyTrackedAttributes = window[TRACKING_CONTAINER].additionalAttributes;
    window[TRACKING_CONTAINER] = __assign(__assign({}, window[TRACKING_CONTAINER]), (_a = {}, _a[ADDITIONAL_ATTRIBUTES] = __assign(__assign({}, currentlyTrackedAttributes), additionalAttributes), _a));
};
/**
 * A helper function to clear out the `omNRTrackingContainer` on the window.
 *
 * @param {*} debug - if set to true, will provide additional console debug information in case of errors.
 */
var clearNewRelicTrackingContainer = function (_a) {
    var _b = _a === void 0 ? {} : _a, _c = _b.debug, debug = _c === void 0 ? false : _c;
    if (windowInvalid()) {
        if (debug) {
            console.error('[NewRelicTracker] `window` is undefined, unable clear New Relic Tracking Container!');
        }
        return;
    }
    // setting this to undefined so window[TRACKING_CONTAINER] will be falsy
    window[TRACKING_CONTAINER] = undefined;
};

export { addAdditionalAttributes, clearNewRelicTrackingContainer, initializeNewRelicTrackingContainer, isNewRelicAvailable, isNewRelicTrackerInitialized, trackError, trackEvent };
//# sourceMappingURL=index.esm.js.map
